import { css, SerializedStyles } from '@emotion/react';

import { styleguideColors as COLORS, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { DEFAULT_META_LIST_STYLES, MetaListStyles } from '@/Client/Widgets/MetaList/metaListStyles';
import { styleguideFontFamily as FONT, withOpacity } from '@kaiizen/kai-shiki/core';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { DEFAULT_VIDEO_LIST_THUMBNAIL_STYLES, VideoListThumbnailStyles } from '../../ScanVideoMedia';
import { ACCENT } from '../../scanVideoMediaStyles';

export type FocusedStripPhotoTileStyles = {
  tile: SerializedStyles;
  selected: SerializedStyles;
  chip: SerializedStyles;
  metaOverlay: SerializedStyles;
  metaWalk: SerializedStyles;
  metaRow: SerializedStyles;
  metaBy: SerializedStyles;
};

export type FocusedStripVideoTileStyles = {
  stripTile: SerializedStyles;
  button: SerializedStyles;
  thumb: VideoListThumbnailStyles;
  playOverlay: SerializedStyles;
  playGlyphBox: SerializedStyles;
  readOverlay: SerializedStyles;
  readOverlayRead: SerializedStyles;
  readOverlayNotRead: SerializedStyles;
};

/**
 * Strip presentation bundled into one object the same way `MetaList` and
 * `VideoPlaybackPanel` keep theirs — a re-skin spreads `DEFAULT_FOCUSED_STRIP_STYLES`
 * and overrides only what differs through the `styles` prop.
 */
export type FocusedStripStyles = {
  header: SerializedStyles;
  title: SerializedStyles;
  countSeparator: SerializedStyles;
  countMetaList: MetaListStyles;
  headerActions: SerializedStyles;
  addAction: SerializedStyles;
  manageAction: SerializedStyles;
  scroller: SerializedStyles;
  empty: SerializedStyles;
  overflowTile: SerializedStyles;
  figure: SerializedStyles;
  caption: SerializedStyles;
  photoTile: FocusedStripPhotoTileStyles;
  videoTile: FocusedStripVideoTileStyles;
};

export const FOCUSED_STRIP_PHOTO_TILE_WIDTH_PX = 170;

const photoTileStyles: FocusedStripPhotoTileStyles = {
  tile: css`
    label: FOCUSED_STRIP_PHOTO_TILE;
    position: relative;
    flex: 0 0 auto;
    width: ${FOCUSED_STRIP_PHOTO_TILE_WIDTH_PX}px;
    aspect-ratio: 4 / 3;
    border: 0;
    overflow: hidden;
    background: ${COLORS.sunken} center / cover no-repeat;
    cursor: pointer;
    padding: 0;

    &:hover {
      outline: 1px solid ${COLORS.ink};
    }
  `,
  selected: css`
    outline: 3px solid var(--textBlue);
    outline-offset: -3px;
  `,
  chip: css`
    label: FOCUSED_STRIP_CHIP;
    position: absolute;
    top: 6px;
    left: 6px;
    max-width: calc(100% - 12px);
    padding: 2px 6px;
    background: ${COLORS.ink};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(10)};
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${COLORS.white};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  metaOverlay: css`
    label: FOCUSED_STRIP_PHOTO_META_OVERLAY;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 14px 8px 7px;
    text-align: left;
    background: linear-gradient(
      to top,
      ${withOpacity(COLORS.ink, 0.94)} 0%,
      ${withOpacity(COLORS.ink, 0.78)} 55%,
      ${withOpacity(COLORS.ink, 0)} 100%
    );
    opacity: 0;
    transform: translateY(4px);
    transition: opacity 0.14s ease, transform 0.14s ease;
    pointer-events: none;

    button:hover > &,
    button:focus-visible > & {
      opacity: 1;
      transform: none;
    }
  `,
  metaWalk: css`
    font-family: ${FONT.mono};
    font-size: ${pxToRem(10)};
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${COLORS.white};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  metaRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    color: ${COLORS.white};
  `,
  metaBy: css`
    font-family: ${FONT.body};
    font-size: ${pxToRem(10)};
    line-height: 1.25;
    color: ${COLORS.white};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
};

export const DEFAULT_FOCUSED_STRIP_STYLES: FocusedStripStyles = {
  header: css`
    label: FOCUSED_STRIP_HEADER;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  `,
  title: css`
    label: FOCUSED_STRIP_TITLE;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(10)};
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${COLORS.textSecondary};
  `,
  countSeparator: css`
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    font-weight: 400;
    color: ${COLORS.textTertiary};
  `,
  countMetaList: {
    root: css([
      DEFAULT_META_LIST_STYLES.root,
      css`
        font: inherit;
        letter-spacing: inherit;
        text-transform: inherit;
        font-weight: 400;
        color: ${COLORS.textTertiary};
      `,
    ]),
    separator: DEFAULT_META_LIST_STYLES.separator,
  },
  headerActions: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  addAction: css`
    label: FOCUSED_STRIP_ADD_ACTION;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 22px;
    color: ${COLORS.ink};
    background: ${COLORS.white};
    border: 1px solid ${COLORS.midtone};
    padding: 0;
    cursor: pointer;
    line-height: 1;
    transition: border-color 0.12s, background 0.12s;

    &:hover {
      border-color: ${COLORS.ink};
      background: ${COLORS.sunken};
    }
  `,
  manageAction: css`
    label: FOCUSED_STRIP_MANAGE_ACTION;
    flex-shrink: 0;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(10)};
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${COLORS.ink};
    background: ${COLORS.white};
    border: 1px solid ${COLORS.midtone};
    padding: 5px 10px;
    cursor: pointer;
    line-height: 1;
    transition: border-color 0.12s, background 0.12s;

    &:hover {
      border-color: ${COLORS.ink};
      background: ${COLORS.sunken};
    }
  `,
  scroller: css`
    label: FOCUSED_STRIP_SCROLLER;
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding: 4px 2px;
  `,
  empty: css`
    label: FOCUSED_STRIP_EMPTY;
    padding: 20px;
    text-align: center;
    font-family: ${FONT.body};
    font-size: ${pxToRem(12)};
    font-style: italic;
    color: ${COLORS.textTertiary};
    background: ${COLORS.sunken};
    border: 1px dashed ${COLORS.stroke};
  `,
  overflowTile: css`
    label: FOCUSED_STRIP_OVERFLOW_TILE;
    flex: 0 0 auto;
    width: 170px;
    aspect-ratio: 4 / 3;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed ${COLORS.stroke};
    background: ${COLORS.sunken};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(10)};
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${COLORS.textSecondary};
    cursor: pointer;
    transition: border-color 0.12s, color 0.12s;

    &:hover {
      border-color: ${COLORS.ink};
      color: ${COLORS.ink};
    }
  `,
  figure: css`
    label: FOCUSED_STRIP_FIGURE;
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin: 0;
    flex: 0 0 auto;
  `,
  caption: css`
    label: FOCUSED_STRIP_CAPTION;
    font-family: ${FONT.body};
    font-size: ${pxToRem(12)};
    font-weight: 400;
    letter-spacing: 0;
    color: ${COLORS.textSecondary};
    white-space: nowrap;
  `,
  photoTile: photoTileStyles,
  videoTile: {
    stripTile: css`
      label: FOCUSED_STRIP_VIDEO_TILE;
      position: relative;
      flex: 0 0 auto;
      width: 170px;
      aspect-ratio: 4 / 3;
      border: 0;
      overflow: hidden;
      background: ${COLORS.sunken};
      cursor: pointer;
      padding: 0;

      &:hover {
        outline: 1px solid ${COLORS.ink};
      }
    `,
    button: css`
      label: FOCUSED_STRIP_VIDEO_BUTTON;
      position: relative;
      display: block;
      width: 100%;
      height: 100%;
      padding: 0;
      border: 0;
      background: transparent;
      cursor: pointer;
      text-align: left;
    `,
    thumb: {
      container: css([
        DEFAULT_VIDEO_LIST_THUMBNAIL_STYLES.container,
        css`
          label: FOCUSED_STRIP_VIDEO_THUMB;
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          flex: none;
        `,
      ]),
      video: DEFAULT_VIDEO_LIST_THUMBNAIL_STYLES.video,
      fallback: DEFAULT_VIDEO_LIST_THUMBNAIL_STYLES.fallback,
    },
    playOverlay: css`
      label: FOCUSED_STRIP_PLAY_OVERLAY;
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    `,
    playGlyphBox: css`
      label: FOCUSED_STRIP_PLAY_GLYPH;
      width: 34px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: 1.5px solid ${COLORS.white};
      color: ${COLORS.white};
      transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;

      button:hover &,
      button:focus-visible & {
        background: ${ACCENT};
        border-color: ${ACCENT};
        color: ${COLORS.ink};
      }
    `,
    readOverlay: css`
      label: FOCUSED_STRIP_VIDEO_READ_OVERLAY;
      position: absolute;
      left: 5px;
      bottom: 5px;
      z-index: 1;
      max-width: calc(100% - 10px);
      padding: 2px 5px;
      font-family: ${FONT.mono};
      font-size: ${pxToRem(8.5)};
      font-weight: ${FONT_WEIGHT.bold};
      letter-spacing: 0.08em;
      text-transform: uppercase;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `,
    readOverlayRead: css`
      label: FOCUSED_STRIP_VIDEO_READ_OVERLAY_READ;
      background: ${withOpacity(COLORS.white, 0.92)};
      color: ${COLORS.ink};
    `,
    readOverlayNotRead: css`
      label: FOCUSED_STRIP_VIDEO_READ_OVERLAY_NOT_READ;
      background: ${ACCENT};
      color: ${COLORS.ink};
    `,
  },
};
